import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { Row, Col, Button } from "reactstrap";
import Select from "react-select";
import makeAnimated from "react-select/animated";
import _ from "lodash";
import { api, constants, UserContext, helpers } from "../utils";
// Imported directly (not via the "../components" barrel) to avoid a circular import through
// index.js, which re-exports this very file — see the identical note in WorkoutColumn.jsx.
import FilterSwitch from "./FilterSwitch";
import { DesktopContainer } from "../layouts";
import WorkoutColumn from "./WorkoutColumn";
import { MdAddCircleOutline } from "react-icons/md";

const MAX_COLUMNS = 7;
const mPlusIcon = (<MdAddCircleOutline className="" />);

const animatedComponents = makeAnimated();

// --- Pure helpers (exported for unit testing without mounting React) -----------------------
//
// `columnReports` shape: { [columnId]: { phaseId, periodId, workoutId, movementIds } | null }
// A `null` entry means the column reported itself as "gone" (unmounted) and should be excluded.

// Maps each movementId to the number of DISTINCT columns whose visible movements include that
// movement. Repeats *within* a single column are de-duped first so they can't inflate the
// cross-column count. Only movements appearing in 2+ columns are kept — that's the definition of
// "repeated": the same movement showing up across columns (not merely the same category).
export function computeMovementDuplicateCounts(columnReports) {
  const columnsByMovement = new Map(); // movementId -> Set<columnId>
  _.forEach(columnReports, (report, columnIdStr) => {
    if (!report || !report.movementIds || !report.movementIds.length) return;
    const columnId = Number(columnIdStr);
    const movementsInColumn = new Set();
    _.forEach(report.movementIds, (movementId) => {
      if (movementId != null) movementsInColumn.add(movementId);
    });
    movementsInColumn.forEach((movementId) => {
      if (!columnsByMovement.has(movementId)) columnsByMovement.set(movementId, new Set());
      columnsByMovement.get(movementId).add(columnId);
    });
  });

  const result = new Map();
  columnsByMovement.forEach((columnSet, movementId) => {
    if (columnSet.size >= 2) result.set(movementId, columnSet.size);
  });
  return result;
}

// Returns the Set of columnIds that share an identical, fully-specified Phase+Period+Workout
// selection with at least one other visible column — flagged (not blocked) per the plan, since
// concurrent autosaves to the same workout could otherwise conflict silently.
export function computeConflictColumnIds(columnReports) {
  const columnsBySelection = new Map(); // "phaseId|periodId|workoutId" -> Set<columnId>
  _.forEach(columnReports, (report, columnIdStr) => {
    if (!report || !report.phaseId || !report.periodId || !report.workoutId) return;
    const columnId = Number(columnIdStr);
    const key = `${report.phaseId}|${report.periodId}|${report.workoutId}`;
    if (!columnsBySelection.has(key)) columnsBySelection.set(key, new Set());
    columnsBySelection.get(key).add(columnId);
  });

  const conflicted = new Set();
  columnsBySelection.forEach((columnSet) => {
    if (columnSet.size >= 2) columnSet.forEach((id) => conflicted.add(id));
  });
  return conflicted;
}
// ---------------------------------------------------------------------------------------------

export default function MultiWorkoutBuilder() {
  const userCtx = useContext(UserContext);
  const isSysAdmin = _.includes(userCtx.currentUser.roleTypeIdList, constants.ROLE_IDS.SYSADMIN);

  const [orgsList, setOrgsList] = useState([]);
  const [selectedOrg, setSelectedOrg] = useState(null);

  const [phaseList, setPhaseList] = useState([]);
  const [periodList, setPeriodList] = useState([]);
  const [workoutList, setWorkoutList] = useState([]);
  const [movementList, setMovementList] = useState([]);

  const [columns, setColumns] = useState([{ id: 1 }]);
  const [columnReports, setColumnReports] = useState({});
  const [highlightDuplicates, setHighlightDuplicates] = useState(true);
  // "Active only" toggle (default on) — mirrors the Program Builder's "Active Workouts" chain filter.
  // When on, the shared Phase list is chain-filtered and each column cascades its own Period/Workout
  // lists on demand (see WorkoutColumn); when off, the shared lists arrive fully populated.
  const [activeWorkoutsOnly, setActiveWorkoutsOnly] = useState(true);

  // Sequential, not Math.random() — a stable id means a column's `key` never changes across
  // renders (so it's never spuriously remounted), and it's simple to reason about while columns
  // are added/removed.
  const nextColumnId = useRef(2);

  useEffect(() => {
    LoadOrgs();
  }, []);

  // A new org invalidates every column's org-scoped Phase/Period/Workout selection — start fresh
  // rather than leaving stale/mismatched selections around.
  useEffect(() => {
    if (!selectedOrg) return;
    nextColumnId.current = 2;
    setColumns([{ id: 1 }]);
    setColumnReports({});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedOrg]);

  // Load the shared reference data whenever the org OR the Active-only toggle changes. Flipping the
  // toggle re-fetches a differently-filtered Phase list (and empties Period/Workout in active mode);
  // the columns are remounted via their `key` (below) so no column keeps a now-invalid selection.
  useEffect(() => {
    if (!selectedOrg) return;
    LoadSharedReferenceData(selectedOrg.value, activeWorkoutsOnly);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedOrg, activeWorkoutsOnly]);

  function LoadOrgs() {
    api
      .post("OrgAdmin/GetOrgSimpleList", { ActiveOnly: true })
      .then((r) => {
        if (r.data && r.data.success) {
          let result = _.map(r.data.message, (x) => ({ label: x.name, value: x.id }));
          if (!isSysAdmin) {
            result = _.filter(result, (x) => x.value === userCtx.currentUser.orgId);
          }
          setOrgsList(result);
          setSelectedOrg(_.find(result, (x) => x.value === userCtx.currentUser.orgId));
        }
      })
      .catch(api.catchHandler);
  }

  // Loads the shared, read-only reference data every column draws its option lists from. Uses the
  // Program Builder's `GetProgramMovementMatrixLists` endpoint so the `activeWorkoutsOnly` chain
  // filter is honoured: in active mode it returns a chain-filtered Phase list with EMPTY
  // Period/Workout lists (each column cascades its own via WorkoutColumn); in off mode all three
  // arrive fully populated. Returns SimpleDto `{id,name}` inside the `{success,message}` envelope,
  // so map via helpers.idNameToValueLabel. `exerciseGroups` is ignored (this is a builder-only
  // screen). Duplicate detection keys on movementId directly (see computeMovementDuplicateCounts).
  function LoadSharedReferenceData(orgId, activeOnly) {
    api
      .fetch(`WorkoutAdmin/GetProgramMovementMatrixLists/${orgId}?activeWorkoutsOnly=${activeOnly}`)
      .then((r) => {
        if (!r.data || !r.data.success) return;
        const vm = r.data.message || {};
        setPhaseList(helpers.idNameToValueLabel(vm.phases || []));
        setPeriodList(helpers.idNameToValueLabel(vm.periods || []));
        setWorkoutList(helpers.idNameToValueLabel(vm.workouts || []));
        setMovementList(
          _.orderBy(helpers.idNameToValueLabel(vm.movements || []), ["label"], ["asc"])
        );
      })
      .catch(api.catchHandler);
  }

  const reportColumn = useCallback((columnId, report) => {
    setColumnReports((prev) => ({ ...prev, [columnId]: report }));
  }, []);

  const addColumn = useCallback(() => {
    setColumns((prev) => {
      if (prev.length >= MAX_COLUMNS) return prev;
      const id = nextColumnId.current++;
      return [...prev, { id }];
    });
  }, []);

  const removeColumn = useCallback((columnId) => {
    setColumns((prev) => prev.filter((c) => c.id !== columnId));
    setColumnReports((prev) => {
      if (!(columnId in prev)) return prev;
      const next = { ...prev };
      delete next[columnId];
      return next;
    });
  }, []);

  const duplicatedMovements = useMemo(
    () => computeMovementDuplicateCounts(columnReports),
    [columnReports]
  );
  const repeatedPatternCount = duplicatedMovements.size;

  const conflictColumnIds = useMemo(
    () => computeConflictColumnIds(columnReports),
    [columnReports]
  );

  return (
    <DesktopContainer>
      <Row className="items-center">
        <Col className="workout-builder-toolbar flex items-center flex-wrap gap-4">
          <span className="workout-builder-title">Multi-Workout Builder</span>
          {selectedOrg ? (
            <>
              <span className="workout-builder-toolbar-count">
                {columns.length} of {MAX_COLUMNS} workouts
              </span>
              <span className="workout-builder-toolbar-sep" aria-hidden="true">|</span>
              <FilterSwitch
                filterName="ActiveWorkoutsOnly"
                displayName="Active only"
                value={activeWorkoutsOnly}
                onChangeCallback={(e) => setActiveWorkoutsOnly(!!e.value)}
                height={20}
                width={40}
                handleDiameter={16}
                onColor="#235ccd"
                offColor="#cbd5e1"
                uncheckedIcon={false}
                checkedIcon={false}
                activeBoxShadow="0 0 2px 3px rgba(35, 92, 205, 0.25)"
              />
              <FilterSwitch
                filterName="HighlightDuplicates"
                displayName="Highlight Duplicates"
                value={highlightDuplicates}
                onChangeCallback={(e) => setHighlightDuplicates(!!e.value)}
                height={20}
                width={40}
                handleDiameter={16}
                onColor="#235ccd"
                offColor="#cbd5e1"
                uncheckedIcon={false}
                checkedIcon={false}
                activeBoxShadow="0 0 2px 3px rgba(35, 92, 205, 0.25)"
              />
              <span className="workout-builder-repeat-count">
                {repeatedPatternCount} repeated pattern{repeatedPatternCount === 1 ? "" : "s"}
              </span>
            </>
          ) : null}
          <div className="workout-builder-org-select shrink-0">
            <Select
              isMulti={false}
              closeMenuOnSelect={true}
              components={animatedComponents}
              options={orgsList || []}
              onChange={setSelectedOrg}
              isClearable={true}
              value={selectedOrg}
              onBlurResetsInput={false}
              onSelectResetsInput={false}
              onCloseResetsInput={false}
              classNamePrefix="react-select"
            />
          </div>
          {selectedOrg ? (
            <Button
              className="workout-builder-add ml-auto"
              onClick={addColumn}
              disabled={columns.length >= MAX_COLUMNS}
              title={columns.length >= MAX_COLUMNS ? "Maximum of 7 workouts reached" : "Add another workout column"}
            >
              {mPlusIcon} Add Workout
            </Button>
          ) : null}
        </Col>
      </Row>
      {selectedOrg ? (
        <>
          <Row className="mt-2">
            <Col xs="12">
              <div className="flex overflow-x-auto gap-4 pb-4">
                {_.map(columns, (col) => (
                  <WorkoutColumn
                    // Include the toggle in the key so flipping "Active only" remounts every column,
                    // clearing any Phase/Period/Workout selection that's no longer valid under the
                    // new filter (mirrors the Program Builder's reconcile-on-refresh behaviour).
                    key={`${activeWorkoutsOnly ? "active" : "all"}-${col.id}`}
                    columnId={col.id}
                    orgId={selectedOrg.value}
                    activeWorkoutsOnly={activeWorkoutsOnly}
                    phaseList={phaseList}
                    periodList={periodList}
                    workoutList={workoutList}
                    mvmtList={movementList}
                    duplicatedMovements={duplicatedMovements}
                    highlightDuplicates={highlightDuplicates}
                    hasSelectionConflict={conflictColumnIds.has(col.id)}
                    canRemove={columns.length > 1}
                    onRemove={removeColumn}
                    onReport={reportColumn}
                  />
                ))}
              </div>
            </Col>
          </Row>
        </>
      ) : null}
    </DesktopContainer>
  );
}
